import { isEmpty } from 'lodash';

// These lists expand built-in role names into frontend-only features; granular backend permissions
// (e.g. orchestration:admin) arrive directly in the auth scope and must not be duplicated here.
const AGENT_ROLE = Object.freeze(['conversation:callRecordingListen', 'conversation:general', 'conversation:read']);
const AGENT_PLUS_ROLE = Object.freeze([...AGENT_ROLE, 'customer:dedicatedHero', 'conversation:callCoach']);

const TEAM_MANAGER = Object.freeze([
  ...AGENT_PLUS_ROLE,
  'adminSettings',
  'agentStatusControl',
  'answers:admin',
  'contactSupport',
  'conversation:socialCommentModeration',
  'customAttributeManagement',
  'inboxManagement',
  'inboxes:viewAll',
  'scheduledReportsManagement',
  'sidekickConversationManagement',
  'teamManagement',
  'topics:admin',
  'viewAnswerPerformance',
  'viewGuidePerformance',
  'reports:read',
  'conversation:outbound',
]);

const SYSTEM_ADMIN_ROLE = Object.freeze([
  ...TEAM_MANAGER,
  'agentConfigurationManagement',
  'agentStatusManagement',
  'appPlatformMcpManagement',
  'campaignsManagement',
  'channelManagement',
  'debuggingView',
  'emailDomainsManagement',
  'facebookAdminSettings',
  'integrationsManagement',
  'ivrConfigurationManagement',
  'mobileNumberLookupManagement',
  'otp:admin',
  'relationshipLabelsManagement',
  'roleManagement',
  'rulesManagement',
  'sidekickConfigurationManagement',
  'skillManagement',
  'ssoConfigurationManagement',
  'userManagement',
]);

const COMPLIANCE_ADMIN_ROLE = Object.freeze([
  ...SYSTEM_ADMIN_ROLE,
  'complianceManagement',
  'manageAttachments',
  'manageVoiceRecordings',
  'conversation:outbound',
]);

const ROLE_FEATURES = Object.freeze({
  agent: AGENT_ROLE,
  'agent-plus': AGENT_PLUS_ROLE,
  analyst: ['reports:read', 'reportBuilder'],
  'answers-admin': ['adminSettings', 'answers:admin'],
  'api-user': ['devManagement'],
  'compliance-admin': COMPLIANCE_ADMIN_ROLE,
  developer: ['debuggingView', 'integrationsManagement'],
  liveboards: ['viewLiveboards'],
  'task-user': ['conversation:callRecordingListen', 'conversation:general', 'conversation:read', 'viewLiveboards'],
  'team-manager': TEAM_MANAGER,
  'system-admin': SYSTEM_ADMIN_ROLE,
});

export default function createRoleFeatures(claims) {
  if (isEmpty(claims)) {
    return {};
  }

  let roleFeatures = (claims.scope || []).reduce((acc, permission) => {
    if (ROLE_FEATURES[permission]) {
      ROLE_FEATURES[permission].forEach(feature => {
        acc[feature] = true;
      });
    } else {
      acc[permission] = true;
    }
    return acc;
  }, {});

  if (claims.ssoEnabled) {
    roleFeatures['sso'] = true;
  }

  return roleFeatures;
}
